// 变体的编辑身份与业务标识分离：标识可以为空或被修改。
let nextVariantId = 0

export const cloneConfig = value => JSON.parse(JSON.stringify(value))
const text = value => String(value ?? '').trim()
const list = value => (Array.isArray(value) ? value : [])

export function createVariant(source = {}) {
  const items = cloneConfig(list(source.items)).sort(
    (left, right) => Number(left.sortNo || 0) - Number(right.sortNo || 0)
  )
  const defaultMaterialNo =
    source.defaultMaterialNo ||
    items.find(item => Number(item.isDefault) === 1)?.materialNo ||
    ''
  return {
    uiId: `variant-${++nextVariantId}`,
    variantKey: source.variantKey || '',
    variantName: source.variantName || '默认配置',
    isDefault: Number(source.isDefault) === 1 ? 1 : 0,
    items,
    defaultMaterialNo,
    previewMaterialNo: defaultMaterialNo || items[0]?.materialNo || ''
  }
}

export function createDraft(detail = {}) {
  const experimentEnabled = Number(detail.experimentEnabled) === 1 ? 1 : 0
  return {
    experimentEnabled,
    experimentId: experimentEnabled ? detail.experimentId ?? '' : '',
    experimentKey: experimentEnabled ? detail.experimentKey || '' : '',
    defaultVariantKey: experimentEnabled ? detail.defaultVariantKey || '' : '',
    variants: experimentEnabled
      ? [...list(detail.variants)]
          .sort(
            (a, b) => Number(a.variantSort || 0) - Number(b.variantSort || 0)
          )
          .map(variant =>
            createVariant({
              ...variant,
              isDefault: variant.variantKey === detail.defaultVariantKey ? 1 : 0
            })
          )
      : [createVariant({ items: detail.items, isDefault: 1 })]
  }
}

export const getDefaultVariant = draft =>
  draft.variants.find(variant => variant.isDefault === 1)

export function setExperimentMode(draft, enabled) {
  const defaultVariant = getDefaultVariant(draft)
  if (!defaultVariant) return false
  Object.assign(defaultVariant, { variantKey: '', variantName: '默认配置' })
  Object.assign(draft, {
    experimentEnabled: enabled ? 1 : 0,
    experimentId: '',
    experimentKey: '',
    defaultVariantKey: '',
    variants: [defaultVariant]
  })
  return true
}

export function getMaterialLanguage(item, languageCode) {
  const languages = list(item?.languages)
  const code = String(languageCode || '').toLowerCase()
  return (
    languages.find(language =>
      [
        language.langCode,
        language.languageCode,
        language.languageId,
        language.code
      ].some(value => String(value ?? '').toLowerCase() === code)
    ) || languages[0]
  )
}

export const getMaterialName = (item, languageCode) => {
  const language = getMaterialLanguage(item, languageCode)
  return language?.title || language?.materialName || item?.materialNo || '-'
}

export function filterCandidates(candidates, items) {
  const used = new Set(items.map(item => item.materialNo))
  return list(candidates).filter(item => !used.has(item.materialNo))
}

export function validateVariantInfo(variant, variants) {
  for (const [field, label] of [
    ['variantName', '变体名称'],
    ['variantKey', '变体标识']
  ]) {
    if (!text(variant[field]) || Array.from(text(variant[field])).length > 64) {
      return { field, message: `${label}必填且最多 64 个字符` }
    }
  }
  if (
    variants.some(
      item =>
        item.uiId !== variant.uiId &&
        text(item.variantKey) === text(variant.variantKey)
    )
  ) {
    return { field: 'variantKey', message: '变体标识不能重复' }
  }
  return null
}

export function validateDraft(draft) {
  if (draft.experimentEnabled) {
    if (
      !/^\d+$/.test(text(draft.experimentId)) ||
      !Number.isSafeInteger(Number(draft.experimentId)) ||
      Number(draft.experimentId) <= 0
    ) {
      return { field: 'experimentId', message: '请输入有效的实验 ID（正整数）' }
    }
    for (const [field, label] of [
      ['experimentKey', '实验标识'],
      ['defaultVariantKey', '默认变体标识']
    ]) {
      if (!text(draft[field]) || Array.from(text(draft[field])).length > 64) {
        return { field, message: `${label}必填且最多 64 个字符` }
      }
    }
  }
  const defaultVariant = getDefaultVariant(draft)
  if (
    !defaultVariant ||
    (draft.experimentEnabled &&
      text(defaultVariant.variantKey) !== text(draft.defaultVariantKey))
  ) {
    return {
      field: 'defaultVariantKey',
      message: '默认变体配置无效，请重新加载站点配置'
    }
  }
  for (const variant of draft.variants) {
    if (draft.experimentEnabled) {
      const error = validateVariantInfo(variant, draft.variants)
      if (error) return { ...error, variantId: variant.uiId }
    }
    const materialNos = variant.items.map(item => item.materialNo)
    const prefix = draft.experimentEnabled ? `${variant.variantName}：` : ''
    let message = ''
    if (materialNos.length < 3 || materialNos.length > 5)
      message = '请配置 3–5 个 Tab'
    else if (
      materialNos.some(no => !no) ||
      new Set(materialNos).size !== materialNos.length
    )
      message = '物料编号不能为空或重复'
    else if (!materialNos.includes(variant.defaultMaterialNo))
      message = '请配置本变体内默认选中的 Tab'
    else if (
      variant.items.filter(item => Number(item.materialType) === 2).length > 1
    )
      message = '大图类型最多配置 1 个'
    if (message) return { variantId: variant.uiId, message: prefix + message }
  }
  return null
}

export function buildPublishPayload(
  draft,
  siteId,
  expectedPublishedVersionNo,
  releaseNote
) {
  const common = {
    siteId: Number(siteId),
    expectedPublishedVersionNo,
    releaseNote: text(releaseNote)
  }
  const buildVariant = variant => ({
    materialNos: variant.items.map(item => item.materialNo),
    defaultMaterialNo: variant.defaultMaterialNo
  })
  if (!draft.experimentEnabled)
    return { ...common, ...buildVariant(getDefaultVariant(draft)) }
  return {
    ...common,
    experimentId: Number(draft.experimentId),
    experimentKey: text(draft.experimentKey),
    defaultVariantKey: text(draft.defaultVariantKey),
    variants: draft.variants.map(variant => ({
      variantKey: text(variant.variantKey),
      variantName: text(variant.variantName),
      ...buildVariant(variant)
    }))
  }
}

// 只同步物料内容，不复制某个变体的 sortNo / isDefault 等配置状态。
const materialFields = [
  'materialType',
  'materialTypeName',
  'languages',
  'jumpType',
  'h5Url',
  'routeId',
  'routeName',
  'routeUrl',
  'selectedBackgroundColor',
  'selectedIconUrl',
  'unselectedIconUrl',
  'selectedTextColor',
  'unselectedTextColor',
  'status',
  'statusName',
  'updateUser',
  'lastUpdateTime'
]

export function updateSharedMaterial(draft, record) {
  const patch = Object.fromEntries(
    materialFields
      .filter(field => field in record)
      .map(field => [field, record[field]])
  )
  draft.variants.forEach(variant => {
    variant.items = variant.items.map(item =>
      item.materialNo === record.materialNo
        ? { ...item, ...cloneConfig(patch) }
        : item
    )
  })
}

function describeItems(previous, current, languageCode) {
  const changes = []
  const before = new Map(
    (previous?.items || []).map(item => [item.materialNo, item])
  )
  const after = new Map(current.items.map(item => [item.materialNo, item]))
  const name = item => getMaterialName(item, languageCode)
  after.forEach((item, no) => {
    if (!before.has(no)) changes.push(`新增装配：${name(item)}`)
    else if (
      materialFields
        .filter(
          field =>
            !['status', 'statusName', 'updateUser', 'lastUpdateTime'].includes(
              field
            )
        )
        .some(
          field =>
            JSON.stringify(before.get(no)[field] ?? null) !==
            JSON.stringify(item[field] ?? null)
        )
    ) {
      changes.push(`编辑物料：${name(item)}`)
    }
  })
  before.forEach((item, no) => {
    if (!after.has(no)) changes.push(`移除装配：${name(item)}`)
  })
  if (
    JSON.stringify([...before.keys()].filter(no => after.has(no))) !==
    JSON.stringify([...after.keys()].filter(no => before.has(no)))
  )
    changes.push('调整 Tab 排序')
  if ((previous?.defaultMaterialNo || '') !== current.defaultMaterialNo) {
    changes.push(
      `默认选中：${
        previous?.defaultMaterialNo
          ? name(before.get(previous.defaultMaterialNo))
          : '未设置'
      } → ${
        current.defaultMaterialNo
          ? name(after.get(current.defaultMaterialNo))
          : '未设置'
      }`
    )
  }
  return changes
}

export function describeDraftChanges(previous, current, languageCode) {
  const changes = []
  if (previous.experimentEnabled !== current.experimentEnabled)
    changes.push(
      current.experimentEnabled
        ? '开启 AB 实验'
        : '关闭 AB 实验，仅保留默认配置'
    )
  if (!current.experimentEnabled) {
    changes.push(
      ...describeItems(
        getDefaultVariant(previous),
        getDefaultVariant(current),
        languageCode
      )
    )
  } else {
    for (const [field, label] of [
      ['experimentId', '实验 ID'],
      ['experimentKey', '实验标识'],
      ['defaultVariantKey', '默认变体标识']
    ]) {
      if (text(previous[field]) !== text(current[field]))
        changes.push(
          `${label}：${text(previous[field]) || '未设置'} → ${text(
            current[field]
          ) || '未设置'}`
        )
    }
    const matched = new Set()
    current.variants.forEach(variant => {
      const before =
        previous.variants.find(item => item.uiId === variant.uiId) ||
        previous.variants.find(item =>
          variant.isDefault === 1
            ? item.isDefault === 1
            : item.variantKey === variant.variantKey
        )
      if (before) matched.add(before.uiId)
      else
        changes.push(
          `新增变体：${variant.variantName}（${variant.variantKey}）`
        )
      if (
        before &&
        (before.variantName !== variant.variantName ||
          before.variantKey !== variant.variantKey)
      )
        changes.push(
          `变体信息：${before.variantName}（${before.variantKey ||
            '未设置'}） → ${variant.variantName}（${variant.variantKey ||
            '未设置'}）`
        )
      changes.push(
        ...describeItems(before, variant, languageCode).map(
          change => `${variant.variantName}：${change}`
        )
      )
    })
    previous.variants
      .filter(variant => !matched.has(variant.uiId))
      .forEach(variant =>
        changes.push(
          `删除变体：${variant.variantName}（${variant.variantKey}）`
        )
      )
  }
  return changes.length ? changes : ['配置无变更']
}

export function getLatestVersionNo(data) {
  const records = list(data?.list)
  if (!records.length && Number(data?.total) === 0) return 0
  const versionNo = records[0]?.versionNo
  if (
    versionNo == null ||
    !Number.isInteger(Number(versionNo)) ||
    Number(versionNo) < 1
  )
    throw new Error('发布版本基准获取失败')
  return Number(versionNo)
}

export function isOnlineVersion(record, detail, experiment) {
  return (
    !!detail &&
    Number(detail.experimentEnabled) === (experiment ? 1 : 0) &&
    Number(detail.versionNo) > 0 &&
    Number(detail.versionNo) === Number(record.versionNo)
  )
}
